<template>
    <!-- Spec 2026-10-14 §5.3: orders of a round (or, seller mode without a
         round, every failed seller order of the last 14 days) that have no
         tracking number, with the human reason, recipient edit and retry. -->
    <el-dialog
        :visible="visible"
        :title="title"
        :append-to-body="true"
        :close-on-click-modal="false"
        width="1160px"
        custom-class="bo-tracking-failures-dialog"
        @update:visible="v => $emit('update:visible', v)"
        @open="load"
    >
        <div data-testid="tracking-failures">
            <div v-if="!tableReady" class="alert alert-secondary py-2 px-3 mb-3" data-testid="tf-table-not-ready">
                {{ $t("tracking_failure.table_not_ready") }}
            </div>

            <el-table
                ref="table"
                v-loading="loading"
                :data="rows"
                row-key="bo_order_id"
                size="small"
                class="tf-table"
                header-row-class-name="thead-light"
                :empty-text="$t('tracking_failure.empty')"
                @selection-change="onSelection"
            >
                <!-- barcode_not_saved rows are never retried: the carrier already issued
                     (and bills) a barcode; a second booking would be a second one. -->
                <el-table-column type="selection" width="40" :selectable="row => !busy && !isBarcodeNotSaved(row)"></el-table-column>
                <el-table-column :label="$t('tracking_failure.col_order')" width="124">
                    <template slot-scope="{ row }">
                        <div class="font-weight-bold">#{{ row.bo_order_id }}</div>
                        <div v-if="row.platform_order_id" class="text-muted tf-small tf-wrap">{{ row.platform_order_id }}</div>
                        <div v-if="!boPackingId && row.bo_packing_id" class="text-muted tf-small">{{ $t("tracking_failure.round_no", { id: row.bo_packing_id }) }}</div>
                    </template>
                </el-table-column>
                <el-table-column :label="$t('tracking_failure.col_recipient')" min-width="100">
                    <template slot-scope="{ row }">
                        <div class="tf-wrap">{{ row.recipient_name || "-" }}</div>
                    </template>
                </el-table-column>
                <el-table-column :label="$t('tracking_failure.col_phone')" width="120" class-name="tf-nowrap" label-class-name="tf-nowrap">
                    <template slot-scope="{ row }">
                        <span :class="{ 'tf-bad': !row.recipient_phone_valid }" data-testid="tf-phone">
                            <i v-if="!row.recipient_phone_valid" class="fa fa-exclamation-triangle mr-1"></i>{{ phoneText(row.recipient_phone) }}
                        </span>
                    </template>
                </el-table-column>
                <el-table-column :label="$t('tracking_failure.col_postcode')" width="96" class-name="tf-nowrap" label-class-name="tf-nowrap">
                    <template slot-scope="{ row }">{{ row.recipient_zipcode || "-" }}</template>
                </el-table-column>
                <el-table-column :label="$t('tracking_failure.col_reason')" min-width="190">
                    <template slot-scope="{ row }">
                        <template v-if="isBarcodeNotSaved(row)">
                            <div class="tf-bns tf-wrap" data-testid="tf-reason">
                                <i class="fa fa-exclamation-triangle mr-1"></i>{{ $t(reasonKey(row.reason_code)) }}
                            </div>
                            <div v-if="row.carrier_message" class="tf-bns-msg" data-testid="tf-bns-message">
                                <div v-if="barcodeOf(row)" class="tf-bns-code" data-testid="tf-bns-barcode">{{ barcodeOf(row) }}</div>
                                {{ row.carrier_message }}
                            </div>
                        </template>
                        <el-tooltip v-else :disabled="!row.carrier_message" :content="row.carrier_message" placement="top" effect="dark">
                            <span class="tf-reason tf-wrap" data-testid="tf-reason">{{ $t(reasonKey(row.reason_code)) }}<i v-if="row.carrier_message" class="fa fa-info-circle ml-1 text-muted"></i></span>
                        </el-tooltip>
                        <div v-if="editedName(row)" class="mt-1">
                            <el-tag size="mini" type="success" data-testid="tf-edited">{{ $t("tracking_failure.edited", { name: editedName(row) }) }}</el-tag>
                        </div>
                    </template>
                </el-table-column>
                <el-table-column :label="$t('tracking_failure.col_attempts')" width="96" align="center" class-name="tf-nowrap" label-class-name="tf-nowrap">
                    <template slot-scope="{ row }">{{ row.attempts || "-" }}</template>
                </el-table-column>
                <el-table-column :label="$t('tracking_failure.col_last')" width="140" class-name="tf-nowrap" label-class-name="tf-nowrap">
                    <template slot-scope="{ row }"><span class="tf-small">{{ timeText(row.last_failed_at) }}</span></template>
                </el-table-column>
                <el-table-column :label="$t('actions')" width="170" align="right" class-name="tf-nowrap" label-class-name="tf-nowrap">
                    <template slot-scope="{ row }">
                        <!-- staff mode PUTs /api/v1/orders/recipient/:id behind mw.IsFullOrder
                             (403 access_denied for a partial role): only offered to that role; the seller
                             mount has its own gate. -->
                        <div class="d-flex flex-column align-items-end tf-actions">
                            <base-button v-if="canEditRecipient" size="sm" type="primary" outline :disabled="busy" data-testid="tf-edit" @click="openEdit(row)">
                                <i class="fa fa-address-card mr-1"></i>{{ $t("tracking_failure.edit_recipient") }}
                            </base-button>
                            <!-- Admins (PUT /orders/tracking-attach/:id is mw.IsAdmin since bo-api #931). -->
                            <base-button
                                v-if="isAdmin && isBarcodeNotSaved(row)"
                                size="sm"
                                type="danger"
                                :disabled="busy"
                                class="tf-attach-btn"
                                data-testid="tf-attach"
                                @click="openAttach(row)"
                            ><i class="fa fa-barcode mr-1"></i>{{ $t("tracking_failure.attach_tracking") }}</base-button>
                        </div>
                    </template>
                </el-table-column>
            </el-table>
        </div>

        <div slot="footer" class="d-flex flex-wrap align-items-center tf-footer">
            <base-button type="secondary" size="sm" :disabled="busy || !retryable.length" data-testid="tf-select-all" @click="selectAll">
                {{ $t("tracking_failure.select_all") }}
            </base-button>
            <span class="text-muted tf-small ml-2">{{ $t("tracking_failure.total", { n: rows.length }) }}</span>
            <base-button
                type="primary"
                class="ml-auto"
                :disabled="busy || !selected.length"
                data-testid="tf-retry"
                @click="retry"
            >
                <i v-if="retrying" class="fa fa-spinner fa-spin mr-1"></i>{{ $t("tracking_failure.retry", { n: selected.length }) }}
            </base-button>
        </div>

        <attach-tracking-dialog
            :visible.sync="attachVisible"
            :row="attachRow"
            @saved="onAttached"
        ></attach-tracking-dialog>

        <recipient-edit-dialog
            :visible.sync="editVisible"
            :order="editRow"
            :mode="mode"
            @saved="onSaved"
        ></recipient-edit-dialog>
    </el-dialog>
</template>

<script>
import { Dialog, Table, TableColumn, Tag, Tooltip, Loading } from "element-ui";
import { PackingService, SellerService } from "@/resource";
import { packingRoundErrorKey } from "@/utils/sellerPackingRound";
import {
    displayPhone,
    formatFailureTime,
    barcodeFromMessage,
    isBarcodeNotSaved,
    retryableRows,
    isRecipientPhoneValid,
    normalizeFailures,
    normalizeRetryResult,
    reasonKey,
    retryBatches,
    sumRetryResults
} from "@/utils/trackingFailure";
import RecipientEditDialog from "./RecipientEditDialog.vue";
import AttachTrackingDialog from "./AttachTrackingDialog.vue";

export default {
    name: "TrackingFailuresModal",
    components: {
        [Dialog.name]: Dialog,
        [Table.name]: Table,
        [TableColumn.name]: TableColumn,
        [Tag.name]: Tag,
        [Tooltip.name]: Tooltip,
        RecipientEditDialog,
        AttachTrackingDialog
    },
    directives: { loading: Loading.directive },
    props: {
        visible: { type: Boolean, default: false },
        // null in seller mode = every failed seller order of the last 14 days.
        boPackingId: { type: [Number, String], default: null },
        mode: { type: String, default: "staff" }
    },
    data() {
        return {
            rows: [],
            tableReady: true,
            loading: false,
            retrying: false,
            selected: [],
            editVisible: false,
            editRow: null,
            attachVisible: false,
            attachRow: null,
            // bo_order_id → name of the editor in this session (before a reload).
            localEdits: {}
        };
    },
    computed: {
        busy() {
            return this.loading || this.retrying;
        },
        // Admin (group 1) or SuperAdmin (99) — the attach route's mw.IsAdmin.
        isAdmin() {
            return !!(this.$store && this.$store.getters["Auth/isAdmin"]);
        },
        retryable() {
            return retryableRows(this.rows);
        },
        canEditRecipient() {
            if (this.mode === "seller") return true;
            return !!(this.$store && this.$store.getters["Auth/isFullOrder"]);
        },
        service() {
            return this.mode === "seller" ? SellerService : PackingService;
        },
        title() {
            return this.boPackingId
                ? this.$t("tracking_failure.title", { id: this.boPackingId })
                : this.$t("tracking_failure.title_all");
        }
    },
    methods: {
        reasonKey(code) {
            return reasonKey(code);
        },
        isBarcodeNotSaved(row) {
            return isBarcodeNotSaved(row);
        },
        barcodeOf(row) {
            return barcodeFromMessage(row && row.carrier_message);
        },
        openAttach(row) {
            this.attachRow = row;
            this.attachVisible = true;
        },
        onAttached() {
            this.$emit("changed");
            this.load();
        },
        timeText(v) {
            return formatFailureTime(v) || "-";
        },
        phoneText(v) {
            return displayPhone(v) || "-";
        },
        editedName(row) {
            return this.localEdits[row.bo_order_id] || row.edited_by_name || "";
        },
        async load() {
            this.loading = true;
            try {
                const params = this.boPackingId ? { bo_packing_id: Number(this.boPackingId) } : {};
                const res = await this.service.trackingFailures(params);
                const out = normalizeFailures(res);
                this.rows = out.rows;
                this.tableReady = out.tableReady;
            } catch (err) {
                this.rows = [];
                this.notifyError(err);
            } finally {
                this.loading = false;
                this.selected = [];
            }
        },
        onSelection(list) {
            this.selected = list || [];
        },
        selectAll() {
            const t = this.$refs.table;
            if (!t) return;
            t.clearSelection();
            // "every failed order" = every one the retry may book.
            this.retryable.forEach(r => t.toggleRowSelection(r, true));
        },
        openEdit(row) {
            this.editRow = row;
            this.editVisible = true;
        },
        onSaved({ id, form }) {
            const me = (this.$store && this.$store.state.Auth && this.$store.state.Auth.user) || {};
            const name = [me.fname, me.lname].filter(Boolean).join(" ") || me.nick_name || this.$t("tracking_failure.edited_me");
            this.$set(this.localEdits, id, name);
            const row = this.rows.find(r => r.bo_order_id === id);
            if (row) {
                Object.assign(row, form, { recipient_phone_valid: isRecipientPhoneValid(form.recipient_phone, row.platform_id) });
            }
            this.$emit("changed");
        },
        async retry() {
            const batches = retryBatches(this.selected);
            if (!batches.length) return;
            this.retrying = true;
            const results = [];
            try {
                for (const b of batches) {
                    const res = await this.service.trackingRetry(b);
                    results.push(normalizeRetryResult(res));
                }
            } catch (err) {
                this.notifyError(err);
            } finally {
                this.retrying = false;
            }
            if (results.length) {
                const sum = sumRetryResults(results);
                let message = this.$t("tracking_failure.retry_result", { ok: sum.minted, fail: sum.failed });
                if (sum.skippedBarcodeNotSaved > 0) {
                    message += " · " + this.$t("tracking_failure.skipped_barcode_not_saved", { n: sum.skippedBarcodeNotSaved });
                }
                this.$notify({
                    type: sum.failed > 0 || sum.skippedBarcodeNotSaved > 0 ? "warning" : "success",
                    message
                });
                this.$emit("changed");
            }
            await this.load();
        },
        notifyError(err) {
            const key = packingRoundErrorKey(err);
            this.$notify({
                type: "danger",
                message: key && this.$te(key) ? this.$t(key) : ((err && err.message) || this.$t("errors.internal"))
            });
        }
    }
};
</script>

<style>
.bo-tracking-failures-dialog {
    max-width: calc(100vw - 32px);
}
.bo-tracking-failures-dialog .el-dialog__body {
    padding-top: 8px;
}
.bo-tracking-failures-dialog .tf-bad {
    color: #c0392b;
    font-weight: 600;
}
.bo-tracking-failures-dialog .tf-nowrap .cell,
.bo-tracking-failures-dialog .tf-nowrap .cell * {
    white-space: nowrap;
    word-break: keep-all;
}
.bo-tracking-failures-dialog .tf-bns {
    color: #a3271f;
    font-weight: 600;
}
.bo-tracking-failures-dialog .tf-bns-msg {
    margin-top: 4px;
    padding: 4px 8px;
    background: #fdecea;
    border: 1px solid #f5c2c0;
    border-radius: 4px;
    color: #7a1d16;
    font-family: SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 0.75rem;
    white-space: normal;
    word-break: break-word;
}
.bo-tracking-failures-dialog .tf-bns-code {
    font-size: 0.9375rem;
    font-weight: 700;
    letter-spacing: 0.5px;
    color: #7a1d16;
}
/* The attach button may wrap (long label) — it overrides the column's nowrap. */
.bo-tracking-failures-dialog .tf-nowrap .cell .tf-attach-btn,
.bo-tracking-failures-dialog .tf-nowrap .cell .tf-attach-btn * {
    white-space: normal;
}
.bo-tracking-failures-dialog .tf-attach-btn {
    max-width: 150px;
    line-height: 1.3;
    text-align: center;
}
.bo-tracking-failures-dialog .tf-actions {
    gap: 4px;
}
.bo-tracking-failures-dialog .tf-actions .btn {
    margin: 0;
}
.bo-tracking-failures-dialog .tf-small {
    font-size: 0.75rem;
}
.bo-tracking-failures-dialog .tf-wrap {
    white-space: normal;
    word-break: break-word;
}
.bo-tracking-failures-dialog .tf-reason {
    cursor: default;
}
.bo-tracking-failures-dialog .tf-footer {
    gap: 8px;
}
</style>
